import { useEffect, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from '@pantry/ui';
import { useTours, useResetTour, type TourWithProgress } from '@pantry/api-client';
import { translateTourField } from '@pantry/i18n';
import { toast } from 'sonner';
import { AcademicCapIcon, ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@pantry/ui';
import { useTourContext } from '../onboarding/useTourContext';

type TourStatus = TourWithProgress['web']['status'];

function StatusBadge({ status }: { status: TourStatus }) {
  const { t } = useLingui();

  const labels: Record<TourStatus, string> = {
    not_started: t`Not started`,
    in_progress: t`In progress`,
    completed: t`Completed`,
    skipped: t`Skipped`,
    reminded: t`Paused`,
  };

  const variants: Record<TourStatus, 'default' | 'secondary' | 'outline'> = {
    not_started: 'outline',
    in_progress: 'secondary',
    completed: 'default',
    skipped: 'outline',
    reminded: 'secondary',
  };

  return (
    <Badge
      variant={variants[status]}
      className={cn(
        'shrink-0',
        status === 'completed' && 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
        status === 'skipped' && 'bg-muted text-muted-foreground border-border'
      )}
    >
      {labels[status]}
    </Badge>
  );
}

function TourItem({ tour }: { tour: TourWithProgress }) {
  const { t, i18n } = useLingui();
  const resetTour = useResetTour();
  const { startTour } = useTourContext();
  const [isStarting, setIsStarting] = useState(false);
  const webStatus = tour.web.status;
  const isNotStarted = webStatus === 'not_started';

  const handleReset = async () => {
    setIsStarting(true);
    try {
      if (!isNotStarted) {
        await resetTour.mutateAsync({ tourId: tour.tourId, deviceType: 'web' });
      }
      await startTour(tour.tourId);
      toast.success(isNotStarted ? t`Tour started.` : t`Tour restarted.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : t`Failed to reset tour`;
      toast.error(message);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-xl border bg-muted/30">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground">
            {translateTourField(i18n, tour.tourId, 'name', tour.name)}
          </span>
          <StatusBadge status={webStatus} />
        </div>
        <p className="text-sm text-muted-foreground mt-0.5 truncate">
          {translateTourField(i18n, tour.tourId, 'description', tour.description)}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={handleReset}
        disabled={resetTour.isPending || isStarting}
        className="shrink-0"
      >
        <ArrowPathIcon
          className={cn('h-4 w-4 mr-1.5', (resetTour.isPending || isStarting) && 'animate-spin')}
        />
        {isNotStarted ? <Trans>Start</Trans> : <Trans>Restart</Trans>}
      </Button>
    </div>
  );
}

export function ToursSection() {
  const { t } = useLingui();
  const { data, isLoading, isError } = useTours();
  const { isActive, currentTourId, currentStepId } = useTourContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const autoExpandedRef = useRef(false);
  const wasExpandedBeforeTourRef = useRef(false);

  const shouldAutoExpandForTour =
    isActive && currentTourId === 'settings-basics' && currentStepId === 'settings-tours';

  useEffect(() => {
    if (shouldAutoExpandForTour && !autoExpandedRef.current) {
      wasExpandedBeforeTourRef.current = isExpanded;
      autoExpandedRef.current = true;
      setIsExpanded(true);
      return;
    }

    if (!shouldAutoExpandForTour && autoExpandedRef.current) {
      autoExpandedRef.current = false;
      setIsExpanded(wasExpandedBeforeTourRef.current);
    }
  }, [shouldAutoExpandForTour, isExpanded]);

  // Hide tours for features not yet available in the web app.
  const hiddenTourIds = new Set(['receipt-scanning', 'shopping-lists']);

  // Filter to only show tours available on web and currently supported in settings.
  const webTours = data?.tours.filter((tour) => tour.web && !hiddenTourIds.has(tour.tourId)) ?? [];

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className="space-y-3 animate-pulse">
        <div className="h-20 bg-muted rounded-xl" />
        <div className="h-20 bg-muted rounded-xl" />
        <div className="h-20 bg-muted rounded-xl" />
      </div>
    );
  } else if (isError || !data) {
    content = (
      <p className="text-sm text-muted-foreground">
        <Trans>Unable to load tours. Please try again later.</Trans>
      </p>
    );
  } else if (webTours.length === 0) {
    content = (
      <p className="text-sm text-muted-foreground">
        <Trans>No tours available.</Trans>
      </p>
    );
  } else {
    content = webTours.map((tour) => <TourItem key={tour.tourId} tour={tour} />);
  }

  return (
    <Card className="rounded-xl" data-tour="settings-tours">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="font-display flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <AcademicCapIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Trans context="section_header">Tours</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Restart guided tours to learn about features you may have missed.</Trans>
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded((prev) => !prev)}
          disabled={shouldAutoExpandForTour}
          aria-expanded={isExpanded}
          className="shrink-0"
        >
          <ChevronDownIcon
            className={cn('h-4 w-4 mr-1 transition-transform', isExpanded && 'rotate-180')}
          />
          {isExpanded
            ? t({ message: 'Hide', context: 'toggle_button' })
            : t({ message: 'Show', context: 'toggle_button' })}
        </Button>
      </CardHeader>
      {isExpanded && <CardContent className="space-y-3">{content}</CardContent>}
    </Card>
  );
}
